/**
 * Affiliate bonuses on the fleet dashboard (TMMPDM-0043, TMMAPI-0105). One row per reseller and
 * currency, from `byReseller[].affiliateBonuses`. Hidden when every list is empty. Never summed:
 * the server publishes no total.
 */
import {
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import type { FleetData } from './useFleetDashboard';

interface AffiliateBonusesTableProps {
  data: FleetData | null;
  locale: string;
}

export function AffiliateBonusesTable({ data, locale }: AffiliateBonusesTableProps) {
  const { t } = useCopy();
  if (!data) return null;

  const rows = data.dashboard.byReseller.flatMap((reseller) =>
    (reseller.affiliateBonuses ?? []).map((bonus) => ({ reseller, bonus })),
  );
  if (rows.length === 0) return null;

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table
          size="small"
          aria-label={t('dashboard.affiliateBonuses.tableAria', 'Affiliate bonuses by reseller')}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.affiliateBonuses.col.reseller', 'Reseller')}</TableCell>
              <TableCell>{t('dashboard.affiliateBonuses.col.currency', 'Currency')}</TableCell>
              <TableCell align="right">
                {t('dashboard.affiliateBonuses.col.bounties', 'Bounties')}
              </TableCell>
              <TableCell align="right">
                {t('dashboard.affiliateBonuses.col.milestones', 'Milestones')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ reseller, bonus }) => {
              const name =
                reseller.resellerId === null
                  ? t('dashboard.byReseller.noReseller', 'No reseller')
                  : (reseller.resellerName ?? `#${reseller.resellerId}`);
              return (
                <TableRow key={`${reseller.resellerId ?? 'none'}-${bonus.currency}`}>
                  <TableCell>
                    {reseller.resellerId === null ? (
                      <Typography variant="body2">{name}</Typography>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={`/partners/${reseller.resellerId}`}
                        variant="body2"
                        underline="hover"
                      >
                        {name}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>{bonus.currency}</TableCell>
                  <TableCell align="right">
                    <Amount minor={bonus.bountyMinor} currency={bonus.currency} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount
                      minor={bonus.milestoneMinor}
                      currency={bonus.currency}
                      locale={locale}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
